import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getTime } from './utils';

const useTimerStore = create(
    persist(
        (set, get) => ({
            timers: {},

            _getKey: (employee_id, task_id) => `${employee_id}_${task_id}`,

            start: (employee_id, task_id) => {
                const key = get()._getKey(employee_id, task_id);
                const startTime = new Date().toISOString();
                set(state => ({
                    timers: {
                        ...state.timers,
                        [key]: {
                            status: 'running',
                            startTime,
                            taskId: task_id,
                            timer: '00:00:00',
                            actionHistory: [{ action: 'start', time: startTime }],
                        }
                    }
                }));
            },

            pause: (employee_id, task_id) => {
                const key = get()._getKey(employee_id, task_id);
                const time = new Date().toISOString();
                const timer = get().timers[key];
                if (!timer) return;

                set(state => ({
                    timers: {
                        ...state.timers,
                        [key]: {
                            ...timer,
                            status: 'paused',
                            actionHistory: [...timer.actionHistory, { action: 'pause', time }]
                        }
                    }
                }));
            },

            resume: (employee_id, task_id) => {
                const key = get()._getKey(employee_id, task_id);
                const time = new Date().toISOString();
                const timer = get().timers[key];
                if (!timer) return;

                set(state => ({
                    timers: {
                        ...state.timers,
                        [key]: {
                            ...timer,
                            status: 'resumed',
                            actionHistory: [...timer.actionHistory, { action: 'resume', time }]
                        }
                    }
                }));
            },

            stop: async (updateTask, employee_id, id, task_id) => {
                const key = get()._getKey(employee_id, task_id);
                const stop_time = new Date();
                const timer = get().timers[key];
                if (!timer) return;

                const { startTime, actionHistory } = timer;
                const simulatedHistory = [
                    ...(actionHistory || []),
                    { action: "stop", time: stop_time.toISOString() }
                ];
                const { time, hoursLogged } = getTime(startTime, stop_time, simulatedHistory);

                await updateTask(id, {
                    stop_time,
                    employee_id,
                    duration: hoursLogged,
                    task_id,
                }, true);

                set(state => {
                    const updated = { ...state.timers };
                    delete updated[key];
                    return { timers: updated };
                });
            },

            updateTimer: (employee_id, task_id) => {
                const key = get()._getKey(employee_id, task_id);
                const timer = get().timers[key];
                if (!timer || !(timer.status === 'running' || timer.status === 'resumed')) return;

                const { startTime, actionHistory } = timer;
                const { time } = getTime(startTime, new Date(), actionHistory);

                set(state => ({
                    timers: {
                        ...state.timers,
                        [key]: {
                            ...timer,
                            timer: time
                        }
                    }
                }));
            },

            reset: (employee_id, task_id) => {
                const key = get()._getKey(employee_id, task_id);
                set(state => {
                    const updated = { ...state.timers };
                    delete updated[key];
                    return { timers: updated };
                });
            },

            getTimerState: (employee_id, task_id) => {
                const key = get()._getKey(employee_id, task_id);
                return get().timers[key] || null;
            }
        }),
        {
            name: 'timer-store',
        }
    )
);

export default useTimerStore;
